import { FC, useCallback, useMemo } from 'react';
import { useClassifierGroupsQuery } from '../../classifier/query/useClassifierGroupsQuery';
import { FormCheckbox, useGetIdFromName } from '../../common/form';
import { extractRowIndexAndColumnName } from '../../common/form/table/tanstack/extractRowIndexAndColumnName';
import { useFormContext, useWatch } from 'react-hook-form';
import { TreeSelectProps } from 'antd';
import { DataObjectValidFormDataType } from '../schema';
import { getMapBy } from '../../common/utils';
import { DataObjectDimensionType } from '../types';
import { ClassifierGroupType } from '../../classifier';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { keepStaleInSeconds } from '../../common/queryClient/utils';

export interface AggregateHierarchyCheckboxProps
  extends Omit<TreeSelectProps, 'id'> {
  name: `${string}.${number}.${string}`;
  id?: string;
  isDraft: boolean;
}

export const AggregateHierarchyCheckbox: FC<AggregateHierarchyCheckboxProps> = (
  props,
) => {
  const getIdFromName = useGetIdFromName();
  const { name, id = getIdFromName(name), isDraft } = props;
  const { dataIndex } = extractRowIndexAndColumnName(name);
  const baseName = `dimensions.${dataIndex}` as const;
  const { setValue, getValues } = useFormContext<DataObjectValidFormDataType>();
  const { data: classifierGroups } = useClassifierGroupsQuery(
    keepStaleInSeconds(5),
  );
  const classifierGroupsByCode = useMemo(
    () => getMapBy(classifierGroups || [], 'code'),
    [classifierGroups],
  );
  const dimension = useWatch({ name: baseName });

  const handleCheckboxChange = useCallback(
    (event: CheckboxChangeEvent) => {
      if (event.target.checked) {
        const dimensions = getValues(`dimensions`).map((dimension, index) => {
          if (index !== dataIndex && dimension.aggregateHierarchy) {
            dimension.aggregateHierarchy = false;
          }
          return dimension;
        });
        setValue(`dimensions`, dimensions);
      }
    },
    [dataIndex, getValues, setValue],
  );

  const isHierarchical = useMemo(() => {
    if (dimension.dimensionType?.code === DataObjectDimensionType.CLASSIFIER) {
      const groupClassifier: ClassifierGroupType | undefined =
        classifierGroupsByCode.get(dimension.classifier?.classifier?.code);
      return (
        (groupClassifier?.categoryHierarchyLevelNumbers || []).length > 1 &&
        dimension.classifier !== null &&
        dimension.classifier.category === null &&
        dimension.classifier.levelNumber === null
      );
    } else if (
      dimension.dimensionType?.code ===
      DataObjectDimensionType.SUBJECT_ATTRIBUTE
    ) {
      const groupClassifier: ClassifierGroupType | undefined =
        classifierGroupsByCode.get(dimension.code);
      return (groupClassifier?.categoryHierarchyLevelNumbers || []).length > 1;
    }
    return false;
  }, [
    dimension.dimensionType?.code,
    dimension.classifier,
    dimension.code,
    classifierGroupsByCode,
  ]);

  if (!isHierarchical) return null;

  return (
    <FormCheckbox
      id={id}
      name={name}
      disabled={!isDraft}
      onChange={handleCheckboxChange}
    />
  );
};
